import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import guardiansService from "../services/guardiansService";
import styles from "../assets/guardianDocumentsManage.module.css";
import Loader from "../components/Loader";

const GuardianDocumentsManage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guardian, setGuardian] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState({
    passport: null,
    bank_statement: [null, null, null],
    employmentReference: null, // Note: field name matches renderDocumentUpload
    payslip: [null, null, null]
  });

  const loadGuardian = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      console.log('🔄 Loading guardian data...');
      const data = await guardiansService.getGuardianById(id);
      console.log('✅ Guardian data loaded:', {
        passport: data?.passport?.length,
        bankStatements: data?.bankStatements?.length,
        employmentReference: data?.employmentReference?.length,
        payslips: data?.payslips?.length
      });
      setGuardian(data);
    } catch (err) {
      console.error("Error loading guardian:", err);
      setError("Failed to load guardian details");
      toast.error("Failed to load guardian details");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGuardian();
  }, [loadGuardian]);

  const handleFileChange = (field, index = null) => (event) => {
    const file = event.target.files[0];
    if (file) {
      setFiles(prev => {
        if (index !== null) {
          const newArray = [...prev[field]];
          newArray[index] = file;
          return { ...prev, [field]: newArray };
        } else {
          return { ...prev, [field]: file };
        }
      });
    }
  };

  const handleRemoveDocument = async (documentId, documentName) => {
    console.log('🗑️ Remove clicked:', { documentId, documentName, guardianId: id });

    if (!documentId) {
      console.error('❌ Document ID is undefined');
      toast.error('Cannot remove document: Document ID is missing');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove "${documentName}"?`)) {
      return;
    }

    try {
      console.log('🗑️ Calling deleteGuardianDocument:', { guardianId: id, documentId });
      const result = await guardiansService.deleteGuardianDocument(id, documentId);
      console.log('✅ Delete result:', result);
      toast.success('Document removed successfully');
      // Force page refresh to get fresh data
      window.location.reload();
    } catch (error) {
      console.error('❌ Error removing document:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to remove document');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Prepare files array for presigned URL generation
      const filesToUpload = [];
      
      // Add passport
      if (files.passport) {
        filesToUpload.push({
          field: 'passport',
          fileName: files.passport.name,
          contentType: files.passport.type,
          file: files.passport
        });
      }

      // Add bank statements
      files.bank_statement.forEach((file, index) => {
        if (file) {
          filesToUpload.push({
            field: 'bank_statement',
            fileName: file.name,
            contentType: file.type,
            file: file,
            index: index
          });
        }
      });

      // Add employment reference
      if (files.employmentReference) {
        filesToUpload.push({
          field: 'employment_reference', // Backend expects 'employment_reference'
          fileName: files.employmentReference.name,
          contentType: files.employmentReference.type,
          file: files.employmentReference
        });
      }

      // Add payslips
      files.payslip.forEach((file, index) => {
        if (file) {
          filesToUpload.push({
            field: 'payslip',
            fileName: file.name,
            contentType: file.type,
            file: file,
            index: index
          });
        }
      });

      if (filesToUpload.length === 0) {
        toast.warning('Please select at least one file to upload');
        return;
      }

      // Generate presigned URLs
      const presignedResponse = await guardiansService.generatePresignedUrls(
        id, 
        filesToUpload.map(f => ({
          field: f.field,
          fileName: f.fileName,
          contentType: f.contentType
        }))
      );

      if (!presignedResponse.success) {
        throw new Error('Failed to generate presigned URLs');
      }

      // Upload files directly to S3 using presigned URLs
      const uploadPromises = filesToUpload.map(async (fileInfo, uploadIndex) => {
        // Match presigned URL by field and fileName - presigned URLs are returned in same order as files sent
        const presignedData = presignedResponse.presignedUrls[uploadIndex];

        if (!presignedData) {
          throw new Error(`No presigned URL found for ${fileInfo.field}${fileInfo.index !== undefined ? `[${fileInfo.index}]` : ''}`);
        }

        // Upload file to S3
        await guardiansService.uploadFileToS3(presignedData.presignedUrl, fileInfo.file);
        
        // Return upload confirmation data (s3Key used for same path as guardian dashboard)
        return {
          s3Key: presignedData.s3Key,
          field: fileInfo.field,
          fileName: fileInfo.fileName,
          fileSize: fileInfo.file.size
        };
      });

      const uploadResults = await Promise.all(uploadPromises);
      
      console.log('✅ Files uploaded to S3:', uploadResults);

      // Confirm uploads with backend (create media records with s3_key = guardian-documents/... path)
      try {
        const confirmResponse = await guardiansService.confirmDocumentUpload(
          id,
          uploadResults.map(result => ({
            s3Key: result.s3Key,
            field: result.field,
            fileName: result.fileName,
            fileSize: result.fileSize
          }))
        );

        if (!confirmResponse.success) {
          console.warn('⚠️ Upload confirmation failed, but files are uploaded to S3');
        } else {
          console.log('✅ Upload confirmations successful:', confirmResponse.updatedMedia);
        }
      } catch (confirmError) {
        console.error('⚠️ Error confirming uploads (files are still uploaded to S3):', confirmError);
        // Don't throw - files are already uploaded to S3
      }
      
      toast.success('Documents uploaded successfully!');
      navigate(`/guardians/${id}`);
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const renderDocumentUpload = (title, field, index = null) => {
    const existingDoc = index !== null
      ? guardian?.[field]?.[index]
      : guardian?.[field]?.[0];

    const currentFile = index !== null
      ? files[field][index]
      : files[field];

    return (
      <div className={styles.documentSection}>
        <h4 className={styles.documentTitle}>{title}</h4>
        <div className="row">
          <div className="col-xs-4">
            {/* Hidden file input - ALWAYS rendered so Replace button works */}
            <input
              type="file"
              id={`file-${field}-${index || 0}`}
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange(field, index)}
              className={styles.fileInput}
              style={{ display: 'none' }}
            />

            {existingDoc ? (
              <div className={styles.existingDocument}>
                <a href={existingDoc.url} target="_blank" rel="noopener noreferrer" className={styles.documentCard}>
                  <div className={styles.documentInfo}>
                    <img src="/assets/images/pdf.png" alt="PDF" className={styles.pdfIcon} />
                    <div>
                      <p className={styles.docName}>{title}</p>
                      <p className={styles.docDate}>Uploaded {new Date(existingDoc.created_at).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                </a>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDocument(existingDoc.id, title);
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className={styles.uploadArea}>
                <label htmlFor={`file-${field}-${index || 0}`} className={styles.fileLabel}>
                  <div className={styles.uploadBox}>
                    <i className="glyphicon glyphicon-cloud-upload"></i>
                    <p>Click to upload {title}</p>
                    {currentFile && <p className={styles.selectedFile}>{currentFile.name}</p>}
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <Loader />;
  // if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container">
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span
          onClick={() => navigate('/dashboard')}
          className={styles.breadcrumbLink}
        >
          Dashboard
        </span>
        <span className={styles.breadcrumbSep}>/</span>
        <span
          onClick={() => navigate('/guardians')}
          className={styles.breadcrumbLink}
        >
          Guardians
        </span>
        <span className={styles.breadcrumbSep}>/</span>
        <span
          onClick={() => navigate(`/guardians/${id}`)}
          className={styles.breadcrumbLink}
        >
          {guardian?.user?.first_name} {guardian?.user?.last_name}
        </span>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>Documents</span>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className={`panel panel-default ${styles.panelForm}`}>
            <div className={`panel-heading ${styles.panelTitle}`}>
              <h3>Manage documents of guardian</h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit} className={styles.documentForm}>

                {/* Passport */}
                {renderDocumentUpload("Passport", "passport")}

                {/* Bank Statements */}
                <div className={styles.documentSection}>
                  <h4 className={styles.documentTitle}>3 bank statements</h4>
                  <div className="row">
                    {[0, 1, 2].map(index => (
                      <div key={index} className="col-xs-4">
                        {/* Hidden file input - ALWAYS rendered so Replace button works */}
                        <input
                          type="file"
                          id={`file-bank_statement-${index}`}
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange('bank_statement', index)}
                          className={styles.fileInput}
                          style={{ display: 'none' }}
                        />

                        {guardian?.bankStatements?.[index] ? (
                          <div className={styles.existingDocument}>
                            <a href={guardian.bankStatements[index].url} target="_blank" rel="noopener noreferrer" className={styles.documentCard}>
                              <div className={styles.documentInfo}>
                                <img src="/assets/images/pdf.png" alt="PDF" className={styles.pdfIcon} />
                                <div>
                                  <p className={styles.docName}>Bank Statement {index + 1}</p>
                                  <p className={styles.docDate}>Uploaded {new Date(guardian.bankStatements[index].created_at).toLocaleDateString('en-GB')}</p>
                                </div>
                              </div>
                            </a>
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveDocument(guardian.bankStatements[index].id, `Bank Statement ${index + 1}`);
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div className={styles.uploadArea}>
                            <label htmlFor={`file-bank_statement-${index}`} className={styles.fileLabel}>
                              <div className={styles.uploadBox}>
                                <i className="glyphicon glyphicon-cloud-upload"></i>
                                <p>Bank Statement {index + 1}</p>
                                {files.bank_statement[index] && <p className={styles.selectedFile}>{files.bank_statement[index].name}</p>}
                              </div>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Employment Reference */}
                {renderDocumentUpload("Employment reference", "employmentReference")}

                {/* Payslips */}
                <div className={styles.documentSection}>
                  <h4 className={styles.documentTitle}>3 recent payslips</h4>
                  <div className="row">
                    {[0, 1, 2].map(index => (
                      <div key={index} className="col-xs-4">
                        {/* Hidden file input - ALWAYS rendered so Replace button works */}
                        <input
                          type="file"
                          id={`file-payslip-${index}`}
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange('payslip', index)}
                          className={styles.fileInput}
                          style={{ display: 'none' }}
                        />

                        {guardian?.payslips?.[index] ? (
                          <div className={styles.existingDocument}>
                            <a href={guardian.payslips[index].url} target="_blank" rel="noopener noreferrer" className={styles.documentCard}>
                              <div className={styles.documentInfo}>
                                <img src="/assets/images/pdf.png" alt="PDF" className={styles.pdfIcon} />
                                <div>
                                  <p className={styles.docName}>Payslip {index + 1}</p>
                                  <p className={styles.docDate}>Uploaded {new Date(guardian.payslips[index].created_at).toLocaleDateString('en-GB')}</p>
                                </div>
                              </div>
                            </a>
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveDocument(guardian.payslips[index].id, `Payslip ${index + 1}`);
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div className={styles.uploadArea}>
                            <label htmlFor={`file-payslip-${index}`} className={styles.fileLabel}>
                              <div className={styles.uploadBox}>
                                <i className="glyphicon glyphicon-cloud-upload"></i>
                                <p>Payslip {index + 1}</p>
                                {files.payslip[index] && <p className={styles.selectedFile}>{files.payslip[index].name}</p>}
                              </div>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <div className={styles.submitSection}>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Submit documentation'}
                    <i className="glyphicon glyphicon-chevron-right"></i>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianDocumentsManage;